import { Link, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { ShoppingBag, Menu as MenuIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import Logo from "./Logo";

export default function Layout({ children }) {
    const { count, total, pulse, deliveryType } = useCart();
    const [pop, setPop] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        if (pulse > 0) {
            setPop(true);
            const t = setTimeout(() => setPop(false), 400);
            return () => clearTimeout(t);
        }
    }, [pulse]);

    useEffect(() => {
        setNavOpen(false);
    }, [location.pathname]);

    const isAdmin = location.pathname.startsWith("/admin") || location.pathname.startsWith("/owner");

    return (
        <div className="min-h-screen bg-cream pb-28">
            {!isAdmin && (
                <header className="sticky top-0 z-40 bg-cream/95 backdrop-blur border-b border-[var(--border-subtle)]">
                    <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl px-4 py-3 flex items-center justify-between">
                        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
                            <Logo size={40} />
                            <div className="leading-tight">
                                <div className="text-text-main font-extrabold text-lg">تسالي سكة 10</div>
                                <div className="text-text-muted text-xs">اطلب • نوصلك</div>
                            </div>
                        </Link>
                        <nav className="hidden md:flex items-center gap-6 text-text-main font-bold">
                            <Link to="/" className="hover:text-brand transition" data-testid="nav-home">الرئيسية</Link>
                            <Link to="/menu" className="hover:text-brand transition" data-testid="nav-menu">المنيو</Link>
                            <Link to="/contact" className="hover:text-brand transition" data-testid="nav-contact">تواصل</Link>
                        </nav>
                        <div className="flex items-center gap-2">
                            <Link
                                to="/cart"
                                data-testid="nav-cart"
                                className={`relative flex items-center gap-2 bg-brand text-white px-3 py-2 rounded-xl font-bold transition ${pop ? "animate-pop" : ""}`}
                            >
                                <ShoppingBag className="w-4 h-4" />
                                <span className="hidden sm:inline text-sm">السلة</span>
                                {count > 0 && (
                                    <span
                                        data-testid="cart-badge"
                                        className="absolute -top-2 -start-2 bg-[var(--mint)] text-text-main text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center border-2 border-cream"
                                    >
                                        {count}
                                    </span>
                                )}
                            </Link>
                            <button
                                className="md:hidden p-2 rounded-lg border border-[var(--border-subtle)] text-text-main"
                                onClick={() => setNavOpen((v) => !v)}
                                data-testid="nav-mobile-toggle"
                                aria-label="القائمة"
                            >
                                {navOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                    {navOpen && (
                        <div className="md:hidden border-t border-[var(--border-subtle)] bg-white">
                            <div className="max-w-md mx-auto px-4 py-3 flex flex-col gap-2 font-bold text-text-main">
                                <Link to="/" data-testid="nav-mobile-home" className="py-2">الرئيسية</Link>
                                <Link to="/menu" data-testid="nav-mobile-menu" className="py-2">المنيو</Link>
                                <Link to="/contact" data-testid="nav-mobile-contact" className="py-2">تواصل</Link>
                            </div>
                        </div>
                    )}
                </header>
            )}

            <main>{children}</main>

            {!isAdmin && count > 0 && !["/checkout", "/cart"].includes(location.pathname) && !location.pathname.startsWith("/pay") && (
                <StickyCartBar count={count} total={total} />
            )}

            {!isAdmin && <Footer />}
        </div>
    );
}

function StickyCartBar({ count, total }) {
    return (
        <div className="fixed bottom-0 inset-x-0 z-50 px-3 pb-3 pointer-events-none">
            <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl pointer-events-auto">
                <Link
                    to="/cart"
                    data-testid="sticky-cart-bar"
                    className="flex items-center justify-between bg-brand text-white rounded-2xl px-4 py-3 shadow-[0_10px_30px_rgba(211,84,0,0.35)]"
                >
                    <div className="flex items-center gap-3">
                        <div className="bg-white/15 rounded-xl px-3 py-1.5 text-sm font-extrabold" data-testid="sticky-cart-count">
                            {count}
                        </div>
                        <span className="font-bold">عرض السلة وإكمال الطلب</span>
                    </div>
                    <div className="font-extrabold" data-testid="sticky-cart-total">
                        {total.toFixed(2)} ر.س
                    </div>
                </Link>
            </div>
        </div>
    );
}

function Footer() {
    return (
        <footer className="mt-16 border-t border-[var(--border-subtle)] py-8 text-center text-text-muted text-sm">
            <div className="max-w-md mx-auto md:max-w-2xl lg:max-w-5xl px-4">
                <div className="flex justify-center mb-3">
                    <Logo size={56} />
                </div>
                <p className="font-bold text-text-main">تسالي سكة 10</p>
                <p className="mt-1">للتواصل: 0509900776</p>
                <p className="mt-2 opacity-70">© {new Date().getFullYear()} جميع الحقوق محفوظة</p>
            </div>
        </footer>
    );
}
